<script lang="ts">
  // Presentational top app bar. The wordmark, the Story 3.2 zoom/pan/fit control
  // cluster (DESIGN.md line 214: the top bar "houses zoom/pan/fit-all/settings"),
  // and (Story 1.6) a settings gear. Every control is a real keyboard-operable
  // <button> with a visible focus ring and a programmatic label (AD-8). The
  // discrete controls dispatch reducer-computed verbs (no projection needed), so
  // they live cleanly here, and announce MAJOR view changes through the shared live
  // region (AD-8, UX-DR12). Continuous drag/wheel paths stay silent (CanvasHost).
  import type { Store } from '../state/store.svelte';
  import { ZOOM_STEP } from '../state/camera';
  import ShareControl from './ShareControl.svelte';

  let {
    store,
    announce,
    onToggleSettings,
    settingsOpen = false,
    buildShareUrl,
    copyText
  }: {
    store: Store;
    announce?: (message: string) => void;
    onToggleSettings?: () => void;
    settingsOpen?: boolean;
    // Story 5.2: the Share affordance lives in the top-bar action cluster (mirroring
    // the settings gear). TopBar only HOSTS it; the copy/URL logic stays inside
    // ShareControl (the two side-effect seams are threaded, so `ui` imports neither
    // `share` nor `platform`).
    buildShareUrl: () => string;
    copyText: (text: string) => Promise<boolean>;
  } = $props();

  function zoomIn(): void {
    store.dispatch({ type: 'ZoomView', factor: ZOOM_STEP });
    announce?.('Zoomed in.');
  }
  function zoomOut(): void {
    store.dispatch({ type: 'ZoomView', factor: 1 / ZOOM_STEP });
    announce?.('Zoomed out.');
  }
  function pan(dir: 'up' | 'down' | 'left' | 'right'): void {
    store.dispatch({ type: 'PanStep', dir });
    const label = { up: 'up', down: 'down', left: 'left', right: 'right' }[dir];
    announce?.(`Panned ${label}.`);
  }
  function fitAll(): void {
    store.dispatch({ type: 'FitAllOrbits' });
    announce?.('Framed all orbits.');
  }
</script>

<header class="top-bar glass-panel" data-testid="top-bar">
  <span class="wordmark">Kepler's Simulator</span>
  <div class="top-bar-actions">
    <div class="view-cluster" role="group" aria-label="View controls">
      <button
        type="button"
        class="view-btn focus-ring"
        data-testid="zoom-in"
        aria-label="Zoom in"
        onclick={zoomIn}
      >
        <span class="material-symbols-outlined" aria-hidden="true">add</span>
      </button>
      <button
        type="button"
        class="view-btn focus-ring"
        data-testid="zoom-out"
        aria-label="Zoom out"
        onclick={zoomOut}
      >
        <span class="material-symbols-outlined" aria-hidden="true">remove</span>
      </button>
      <div class="pan-cluster" role="group" aria-label="Pan view">
        <button
          type="button"
          class="view-btn focus-ring"
          data-testid="pan-up"
          aria-label="Pan up"
          onclick={() => pan('up')}
        >
          <span class="material-symbols-outlined" aria-hidden="true">keyboard_arrow_up</span>
        </button>
        <button
          type="button"
          class="view-btn focus-ring"
          data-testid="pan-left"
          aria-label="Pan left"
          onclick={() => pan('left')}
        >
          <span class="material-symbols-outlined" aria-hidden="true">keyboard_arrow_left</span>
        </button>
        <button
          type="button"
          class="view-btn focus-ring"
          data-testid="pan-right"
          aria-label="Pan right"
          onclick={() => pan('right')}
        >
          <span class="material-symbols-outlined" aria-hidden="true">keyboard_arrow_right</span>
        </button>
        <button
          type="button"
          class="view-btn focus-ring"
          data-testid="pan-down"
          aria-label="Pan down"
          onclick={() => pan('down')}
        >
          <span class="material-symbols-outlined" aria-hidden="true">keyboard_arrow_down</span>
        </button>
      </div>
      <button
        type="button"
        class="view-btn fit-btn focus-ring"
        data-testid="fit-all"
        aria-label="Fit all orbits"
        onclick={fitAll}
      >
        <span class="material-symbols-outlined" aria-hidden="true">fit_screen</span>
      </button>
    </div>
    <ShareControl {announce} {buildShareUrl} {copyText} />
    <button
      type="button"
      class="gear-btn focus-ring"
      data-testid="settings-gear"
      aria-label="Graphics settings and diagnostics"
      aria-haspopup="dialog"
      aria-expanded={settingsOpen}
      onclick={() => onToggleSettings?.()}
    >
      <span class="material-symbols-outlined" aria-hidden="true">settings</span>
    </button>
  </div>
</header>

<style>
  .top-bar {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: var(--top-bar-height);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 var(--edge-margin);
    border-radius: 0;
    border-top: none;
    border-left: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    z-index: 10;
  }

  .wordmark {
    font-weight: 700;
    font-size: 20px;
    letter-spacing: -0.01em;
    color: var(--on-surface);
  }

  .top-bar-actions {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .view-cluster {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding-right: 8px;
    margin-right: 4px;
    border-right: 1px solid rgba(255, 255, 255, 0.08);
  }

  .pan-cluster {
    display: inline-grid;
    grid-template-columns: repeat(2, auto);
    gap: 2px;
  }

  .view-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border: none;
    border-radius: var(--radius-full);
    background: transparent;
    color: var(--on-surface);
    cursor: pointer;
  }

  .view-btn:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  .view-btn .material-symbols-outlined {
    font-size: 20px;
  }

  .gear-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: none;
    border-radius: var(--radius-full);
    background: transparent;
    color: var(--on-surface);
    cursor: pointer;
  }

  .gear-btn:hover {
    background: rgba(255, 255, 255, 0.08);
  }
</style>
